import Markdown, { type Components } from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import rehypeSanitize, { type Options as Schema } from 'rehype-sanitize'
import remarkGfm from 'remark-gfm'

import { storySanitizeSchema } from '@/lib/markdown-sanitize'

/**
 * Renders story Markdown (the Heisig corpus, Kanjido stories, Koohii
 * community stories) through the one pipeline every story surface uses:
 * GFM, then raw HTML parsing for the corpus's inline `<i>` tags, then
 * sanitizing against an allowlist.
 *
 * @remarks
 * The wrapper is optional on purpose. With `className`, the output sits in
 * a `<div>` carrying that site's typography (`[&_strong]`, `[&_p]`,
 * line-clamp and so on). Without it, the paragraphs render bare, for sites
 * where they must stay direct children of an outer `space-y-*` container
 * or of a semantic wrapper like an `<li>`.
 */
export function StoryMarkdown({
    children,
    className,
    schema = storySanitizeSchema,
    components,
}: {
    children: string
    className?: string
    /** Community (Koohii) stories pass `communitySanitizeSchema`. */
    schema?: Schema
    components?: Components
}) {
    const markdown = (
        <Markdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw, [rehypeSanitize, schema]]}
            components={components}
        >
            {children}
        </Markdown>
    )
    return className ? <div className={className}>{markdown}</div> : markdown
}
